/**
 * Game-state model, the play layer the UI binds to.
 *
 * Recreates Good Sudoku's interaction model:
 *   - placing values (with optional immediate mistake-flagging),
 *   - the dual note system: pencil candidates plus Accent notes (positive,
 *     "the digit probably goes here") and Cross-out notes (negative,
 *     "this digit is eliminated here"),
 *   - auto-candidates that fill every legal pencil mark,
 *   - full undo/redo,
 *   - peer / same-digit / conflict highlighting helpers,
 *   - mistake detection and rewind to the last correct state.
 *
 * The engine is UI-framework-agnostic and fully deterministic.
 */

import {
  ALL_DIGITS_MASK,
  CELL_COUNT,
  Grid,
  PEERS,
  UNIT_INDICES_OF_CELL,
  UNITS,
  candidatesForCell,
  digitsOf,
  firstDigit,
  maskOf,
  popcount,
} from "./board.js";
import { DifficultyId } from "./difficulty.js";
import { GeneratedPuzzle } from "./generator.js";
import { Hint, getHint } from "./hint.js";

export type NoteKind = "pencil" | "accent" | "crossout";

export interface CellView {
  index: number;
  value: number; // 0 = empty
  given: boolean;
  pencil: number; // bitmask
  accent: number; // bitmask
  crossout: number; // bitmask
  /** True when assist is on and value disagrees with the solution. */
  mistake: boolean;
}

interface Snapshot {
  values: Int8Array;
  pencil: Uint16Array;
  accent: Uint16Array;
  crossout: Uint16Array;
}

export interface GameOptions {
  /** Flag wrong entries against the known solution immediately. Default true. */
  assist?: boolean;
  /** Automatically maintain pencil candidates as you play. Default false. */
  autoCandidates?: boolean;
  /**
   * After a correct entry, cascade-fill every cell that becomes forced, a cell
   * left with a single candidate, or a digit with a single home in a unit —
   * rippling outward like the iOS game. Default true.
   */
  autoSolve?: boolean;
}

export interface PlaceResult {
  changed: boolean;
  /** True if the placed value contradicts the solution (assist mode). */
  mistake: boolean;
  /** True if the board is now correctly completed. */
  solved: boolean;
  /**
   * Cells filled by the auto-solve cascade, in the order they resolved, so the
   * UI can animate the chain reaction. Empty when nothing cascaded.
   */
  autoFilled: number[];
}

export class SudokuGame {
  readonly difficulty: DifficultyId;
  readonly givens: Int8Array;
  readonly solution: Grid;

  private values: Int8Array;
  private pencil: Uint16Array;
  private accent: Uint16Array;
  private crossout: Uint16Array;

  private undoStack: Snapshot[] = [];
  private redoStack: Snapshot[] = [];

  assist: boolean;
  autoCandidates: boolean;
  autoSolve: boolean;

  hintsUsed = 0;
  mistakeCount = 0;

  constructor(puzzle: GeneratedPuzzle, options: GameOptions = {}) {
    this.difficulty = puzzle.difficulty;
    this.givens = puzzle.puzzle.slice() as Int8Array;
    this.solution = puzzle.solution.slice() as Grid;
    this.values = puzzle.puzzle.slice() as Int8Array;
    this.pencil = new Uint16Array(CELL_COUNT);
    this.accent = new Uint16Array(CELL_COUNT);
    this.crossout = new Uint16Array(CELL_COUNT);
    this.assist = options.assist ?? true;
    this.autoCandidates = options.autoCandidates ?? false;
    this.autoSolve = options.autoSolve ?? true;
    if (this.autoCandidates) this.recomputePencil();
  }

  // --- snapshots / undo-redo ------------------------------------------------

  private snapshot(): Snapshot {
    return {
      values: this.values.slice(),
      pencil: this.pencil.slice(),
      accent: this.accent.slice(),
      crossout: this.crossout.slice(),
    };
  }

  private pushUndo(): void {
    this.undoStack.push(this.snapshot());
    this.redoStack.length = 0;
    // Keep history bounded.
    if (this.undoStack.length > 500) this.undoStack.shift();
  }

  private restore(s: Snapshot): void {
    this.values = s.values;
    this.pencil = s.pencil;
    this.accent = s.accent;
    this.crossout = s.crossout;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): boolean {
    const prev = this.undoStack.pop();
    if (!prev) return false;
    this.redoStack.push(this.snapshot());
    this.restore(prev);
    return true;
  }

  redo(): boolean {
    const next = this.redoStack.pop();
    if (!next) return false;
    this.undoStack.push(this.snapshot());
    this.restore(next);
    return true;
  }

  // --- queries --------------------------------------------------------------

  isGiven(i: number): boolean {
    return this.givens[i] !== 0;
  }

  getValue(i: number): number {
    return this.values[i];
  }

  getCell(i: number): CellView {
    return {
      index: i,
      value: this.values[i],
      given: this.givens[i] !== 0,
      pencil: this.pencil[i],
      accent: this.accent[i],
      crossout: this.crossout[i],
      mistake:
        this.assist &&
        this.values[i] !== 0 &&
        this.givens[i] === 0 &&
        this.values[i] !== this.solution[i],
    };
  }

  /** Snapshot of the whole board for rendering. */
  cells(): CellView[] {
    const out: CellView[] = [];
    for (let i = 0; i < CELL_COUNT; i++) out.push(this.getCell(i));
    return out;
  }

  /** Current values as a plain grid (filled cells only). */
  toGrid(): Grid {
    return this.values.slice() as Grid;
  }

  /** Legal digits for a cell given current placements (cell-first numpad). */
  candidatesFor(i: number): number[] {
    if (this.values[i] !== 0) return [];
    return digitsOf(candidatesForCell(this.toGrid(), i));
  }

  /** How many of a digit are still unplaced (numpad badges / disabling). */
  remainingCount(digit: number): number {
    let placed = 0;
    for (let i = 0; i < CELL_COUNT; i++) if (this.values[i] === digit) placed++;
    return 9 - placed;
  }

  /** Cells that violate Sudoku rules (duplicate in a unit). */
  conflicts(): Set<number> {
    const bad = new Set<number>();
    for (const unit of UNITS) {
      const seen = new Map<number, number>();
      for (const c of unit) {
        const v = this.values[c];
        if (v === 0) continue;
        if (seen.has(v)) {
          bad.add(c);
          bad.add(seen.get(v)!);
        } else {
          seen.set(v, c);
        }
      }
    }
    return bad;
  }

  /** Cells whose value disagrees with the solution (assist diagnostics). */
  mistakes(): number[] {
    const out: number[] = [];
    for (let i = 0; i < CELL_COUNT; i++) {
      if (this.givens[i] === 0 && this.values[i] !== 0 && this.values[i] !== this.solution[i])
        out.push(i);
    }
    return out;
  }

  isComplete(): boolean {
    for (let i = 0; i < CELL_COUNT; i++) if (this.values[i] === 0) return false;
    return true;
  }

  isSolved(): boolean {
    for (let i = 0; i < CELL_COUNT; i++) if (this.values[i] !== this.solution[i]) return false;
    return true;
  }

  /** Progress 0..1 over the cells that started empty. */
  progress(): number {
    let empties = 0;
    let correct = 0;
    for (let i = 0; i < CELL_COUNT; i++) {
      if (this.givens[i] !== 0) continue;
      empties++;
      if (this.values[i] === this.solution[i]) correct++;
    }
    return empties === 0 ? 1 : correct / empties;
  }

  // --- highlighting helpers -------------------------------------------------

  /** Peers of a cell (its row, column and box), for selection highlighting. */
  peersOf(i: number): readonly number[] {
    return PEERS[i];
  }

  /** All cells holding a given digit as a value (same-digit highlight). */
  cellsWithValue(digit: number): number[] {
    const out: number[] = [];
    for (let i = 0; i < CELL_COUNT; i++) if (this.values[i] === digit) out.push(i);
    return out;
  }

  unitsOfCell(i: number): readonly number[] {
    return UNIT_INDICES_OF_CELL[i];
  }

  // --- mutations ------------------------------------------------------------

  /** Place (or clear, with digit 0) a value in a non-given cell. */
  setValue(i: number, digit: number): PlaceResult {
    if (this.givens[i] !== 0) return { changed: false, mistake: false, solved: false, autoFilled: [] };
    if (this.values[i] === digit) {
      // Tapping the same digit again clears it (Good Sudoku toggle behaviour).
      return this.clear(i);
    }
    this.pushUndo();
    this.placeValueRaw(i, digit);
    if (this.autoCandidates) this.recomputePencil();

    const mistake = this.assist && digit !== 0 && digit !== this.solution[i];
    if (mistake) this.mistakeCount++;

    // Cascade only from a correct placement, so the chain reaction is always
    // sound (a wrong guess never auto-fills the board).
    let autoFilled: number[] = [];
    if (digit !== 0 && this.autoSolve && digit === this.solution[i]) {
      autoFilled = this.cascadeAutoSolve();
      if (this.autoCandidates) this.recomputePencil();
    }
    return { changed: true, mistake, solved: this.isSolved(), autoFilled };
  }

  /** Set a value and tidy notes, no snapshot, no cascade. */
  private placeValueRaw(i: number, digit: number): void {
    this.values[i] = digit;
    this.pencil[i] = 0;
    this.accent[i] = 0;
    this.crossout[i] = 0;
    if (digit !== 0) {
      const clearBit = ~maskOf(digit);
      for (const p of PEERS[i]) {
        this.pencil[p] &= clearBit;
        this.accent[p] &= clearBit;
      }
    }
  }

  /**
   * Fill every cell that is now forced, rippling outward. A cell is forced when
   * it has a single legal candidate (a naked single, or pencil notes the player
   * has narrowed to one), or when a digit has only one possible home left in a
   * row, column or box (a hidden single). Each fill is verified against the
   * solution, so the cascade is always correct. Returns the cells in the order
   * they resolved, for the UI's chain-reaction animation.
   */
  private cascadeAutoSolve(): number[] {
    const filled: number[] = [];
    const grid = this.values as Grid;
    const cand = new Uint16Array(CELL_COUNT);

    for (let guard = 0; guard < CELL_COUNT; guard++) {
      let placedAny = false;
      for (let c = 0; c < CELL_COUNT; c++)
        cand[c] = this.values[c] === 0 ? candidatesForCell(grid, c) : 0;

      // Naked singles.
      for (let c = 0; c < CELL_COUNT; c++) {
        if (this.values[c] !== 0) continue;
        const legal = cand[c];
        let d = 0;
        if (popcount(legal) === 1) d = firstDigit(legal);
        else if (this.pencil[c] !== 0 && popcount(this.pencil[c] & legal) === 1)
          d = firstDigit(this.pencil[c] & legal);
        if (d !== 0 && this.solution[c] === d) {
          this.placeValueRaw(c, d);
          filled.push(c);
          placedAny = true;
        }
      }

      // Hidden singles.
      for (const unit of UNITS) {
        for (let d = 1; d <= 9; d++) {
          const bit = maskOf(d);
          let home = -1;
          let count = 0;
          let already = false;
          for (const c of unit) {
            if (this.values[c] === d) {
              already = true;
              break;
            }
            if (this.values[c] === 0 && cand[c] & bit) {
              home = c;
              count++;
            }
          }
          if (!already && count === 1 && this.solution[home] === d) {
            this.placeValueRaw(home, d);
            filled.push(home);
            placedAny = true;
          }
        }
      }

      if (!placedAny) break;
    }
    return filled;
  }

  clear(i: number): PlaceResult {
    if (this.givens[i] !== 0) return { changed: false, mistake: false, solved: false, autoFilled: [] };
    if (this.values[i] === 0 && this.pencil[i] === 0 && this.accent[i] === 0 && this.crossout[i] === 0)
      return { changed: false, mistake: false, solved: false, autoFilled: [] };
    this.pushUndo();
    this.values[i] = 0;
    this.pencil[i] = 0;
    this.accent[i] = 0;
    this.crossout[i] = 0;
    if (this.autoCandidates) this.recomputePencil();
    return { changed: true, mistake: false, solved: false, autoFilled: [] };
  }

  /**
   * Toggle a note of the given kind for a digit in a cell. A digit can occupy
   * only one note state at a time, so setting one clears the others. Toggling
   * the active state turns it off.
   */
  toggleNote(i: number, digit: number, kind: NoteKind): boolean {
    if (this.givens[i] !== 0 || this.values[i] !== 0) return false;
    const bit = maskOf(digit);
    this.pushUndo();
    const active =
      (kind === "pencil" && this.pencil[i] & bit) ||
      (kind === "accent" && this.accent[i] & bit) ||
      (kind === "crossout" && this.crossout[i] & bit);
    // Clear the digit from all note states first.
    this.pencil[i] &= ~bit;
    this.accent[i] &= ~bit;
    this.crossout[i] &= ~bit;
    if (!active) {
      if (kind === "pencil") this.pencil[i] |= bit;
      else if (kind === "accent") this.accent[i] |= bit;
      else this.crossout[i] |= bit;
    } else {
      // It was active and we cleared it, that's the toggle-off; undo the
      // snapshot push only if nothing changed is impossible here (it changed).
    }
    return true;
  }

  /** Auto-candidates: fill legal pencil marks for every empty cell. */
  autoFillNotes(): void {
    this.pushUndo();
    this.recomputePencil();
  }

  private recomputePencil(): void {
    const grid = this.toGrid();
    for (let i = 0; i < CELL_COUNT; i++) {
      if (this.values[i] !== 0) {
        this.pencil[i] = 0;
        continue;
      }
      // Keep accented/crossed digits out of the auto pencil layer.
      const legal = candidatesForCell(grid, i);
      this.pencil[i] = legal & ~this.crossout[i] & ~this.accent[i] & ALL_DIGITS_MASK;
    }
  }

  clearAllNotes(): void {
    this.pushUndo();
    this.pencil.fill(0);
    this.accent.fill(0);
    this.crossout.fill(0);
  }

  /** Reset every non-given cell (values and notes) back to the givens. Undoable. */
  clearBoard(): void {
    this.pushUndo();
    for (let i = 0; i < CELL_COUNT; i++) {
      if (this.givens[i] === 0) this.values[i] = 0;
      this.pencil[i] = 0;
      this.accent[i] = 0;
      this.crossout[i] = 0;
    }
    if (this.autoCandidates) this.recomputePencil();
  }

  setAutoCandidates(on: boolean): void {
    this.autoCandidates = on;
    if (on) this.autoFillNotes();
  }

  // --- hints / rewind -------------------------------------------------------

  /** The next logical move with a teaching explanation. */
  hint(maxTier = 5): Hint | null {
    const h = getHint(this.toGrid(), maxTier);
    if (h) this.hintsUsed++;
    return h;
  }

  /** Fill a single correct cell (the next logical placement) as a hint reveal. */
  revealHintCell(): number | null {
    const h = getHint(this.toGrid());
    if (!h || h.step.placements.length === 0) {
      // No direct placement available; reveal the first empty correct cell.
      for (let i = 0; i < CELL_COUNT; i++) {
        if (this.values[i] === 0) {
          this.setValue(i, this.solution[i]);
          return i;
        }
      }
      return null;
    }
    const p = h.step.placements[0];
    this.setValue(p.cell, p.digit);
    return p.cell;
  }

  /** Roll back to the most recent state with no mistakes (rewind-to-correct). */
  rewindToCorrect(): boolean {
    let rewound = false;
    while (this.mistakes().length > 0 && this.canUndo()) {
      this.undo();
      rewound = true;
    }
    return rewound;
  }

  // --- persistence ----------------------------------------------------------

  serialize(): string {
    return JSON.stringify({
      difficulty: this.difficulty,
      givens: Array.from(this.givens),
      solution: Array.from(this.solution),
      values: Array.from(this.values),
      pencil: Array.from(this.pencil),
      accent: Array.from(this.accent),
      crossout: Array.from(this.crossout),
      assist: this.assist,
      autoCandidates: this.autoCandidates,
      autoSolve: this.autoSolve,
      hintsUsed: this.hintsUsed,
      mistakeCount: this.mistakeCount,
    });
  }

  static deserialize(json: string): SudokuGame {
    const o = JSON.parse(json);
    const puzzle: GeneratedPuzzle = {
      difficulty: o.difficulty,
      puzzle: Int8Array.from(o.givens) as Grid,
      solution: Int8Array.from(o.solution) as Grid,
      grade: {
        solvableByLogic: true,
        tier: 0,
        difficulty: o.difficulty,
        techniqueCounts: {},
        techniquesUsed: [],
      },
      clues: o.givens.filter((v: number) => v !== 0).length,
      seed: "restored",
      attempts: 0,
    };
    const game = new SudokuGame(puzzle, {
      assist: o.assist,
      autoCandidates: false,
    });
    game.values = Int8Array.from(o.values);
    game.pencil = Uint16Array.from(o.pencil);
    game.accent = Uint16Array.from(o.accent);
    game.crossout = Uint16Array.from(o.crossout);
    game.autoCandidates = o.autoCandidates;
    game.autoSolve = o.autoSolve ?? true;
    game.hintsUsed = o.hintsUsed ?? 0;
    game.mistakeCount = o.mistakeCount ?? 0;
    return game;
  }
}
